<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search Interest Type..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
            <button type="button" :disabled="loading" class="theme-button px-5"
              @click="resetForm(); isEditing = false; editingId = null; intConfigDialog = true">
              <span v-if="!loading"><v-icon start>mdi-content-save-cog-outline</v-icon> Save Interest Type </span>
            </button>
          </div>

          <!-- PrimeVue DataTable with search ---NB: add showGridlines to show column lines-->
          <DataTable v-model:filters="filters" :value="interestType" paginator :rows="10" dataKey="uuid" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" selectionMode="single" :globalFilterFields="['name']">

            <!-- Empty table -->
            <template #empty>
              No interest type found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading products...
            </template>

            <!-- Product Column -->
            <Column header="Interest Type">
              <template #body="slotProps">
                <div class="product-cell">
                  <img
                    :src="slotProps.data.product_type_image ? `${STORAGE_URL}/${slotProps.data.product_type_image}` : defaultImage"
                    class="product-image" />
                  <div>
                    <div class="product-name">{{ slotProps.data.name }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Operation Column -->
            <Column header="Operation">
              <template #body="slotProps">
                {{ slotProps.data.operation }}
              </template>
            </Column>

            <!-- Status Column -->
            <Column header="Added On">
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.created_at) }}
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="" style="width:120px; border-radius: 10px;">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEditModal(slotProps.data)">
                      Edit {{ slotProps.data.name }} Details
                    </v-list-item>
                  </v-list>

                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <v-dialog v-model="intConfigDialog" max-width="650">
          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">
              {{ isEditing ? 'Edit Interest Type' : 'Create Interest Type' }}
            </v-card-title>

            <form @submit.prevent="handleCreatrInterest" class="space-y-5">


              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Name</label>
                <v-text-field v-model="form.name" type="text" variant="outlined" placeholder="Interest type e.g High" />
              </div>

              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Operation</label>
                <v-textarea v-model="form.operation" variant="outlined" rows="2" />
              </div>

              <!-- Rate -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Rate</label>
                <v-text-field v-model="form.rate" type="number" variant="outlined" :rules="[
                  v => !!v || 'Value is required',
                  v => v > 0 || 'Must be greater than 0'
                ]" />
              </div>

              <!-- ACTIONS -->
              <div class="flex justify-center align-center  gap-3 pt-4"
                style="display:flex; justify-content:center; align-items:center; gap:12px; padding-top:16px;">
                <v-btn variant="outlined" @click="intConfigDialog = false">
                  Cancel
                </v-btn>
                <v-btn color="black" type="submit" :loading="intLoading" :disabled="!isFormValid">
                  {{ isEditing ? 'Update Interest' : 'Save Interest' }}
                </v-btn>
              </div>

            </form>
          </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
          snackbar.message }}
          <template #actions>
            <v-btn variant="text" @click="snackbar.show = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { useInvStore } from "@/store/investment"
import { STORAGE_URL } from "@/api/env"
import avatarImage from '@/assets/icon.png'
import { formatDateTime, getAllErrors } from "@/util"
import { addInterest, updateInterest } from "@/api/investment"

//const auth = useAuthStore()
const interestRateType = useInvStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })
const defaultImage = avatarImage
const intLoading = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const intConfigDialog = ref(false)

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Interest */
const interestType = computed(() => interestRateType.interestType || [])

/* Stats */
// const activeProducts = computed(() => products.value.filter(p => p.is_active).length)
// const inactiveProducts = computed(() => products.value.filter(p => !p.is_active).length)

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  try {
    await interestRateType.loadInterestType()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}


const form = ref({
  name: "",
  operation: "#000000",
  rate: ""
})

/**
 * FORM VALIDATION STATE
 */
const isFormValid = computed(() => {
  return (
    form.value.name &&
    form.value.operation &&
    form.value.rate
  )
})

/**
 * RESET FORM
 */
const resetForm = () => {
  form.value = {
    name: "",
    operation: null,
    rate: "",
  }
}

//Function to create or edit
const handleCreatrInterest = async () => {
  intLoading.value = true

  try {
    const payload = {
      name: form.value.name,
      operation: form.value.operation,
      rate: form.value.rate,
    }

    //await executeFeeConfig(payload)
    if (isEditing.value) {
      // update api
      await updateInterest(editingId.value, payload)
    } else {
      // create api
      await addInterest(payload)
    }

    // reload updated data
    await interestRateType.loadInterestType()

    //close the modal
    //feeConfigDialog.value = false
    showSnackbar("Interest type saved successfully")

    //resetForm()
    //feeConfigDialog.value = false

  } catch (e) {


    const msg = getAllErrors(e)
    showSnackbar(
      msg || "Failed to save Interest",
      "error"
    )
  } finally {
    intLoading.value = false
  }
}

//editing modal
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.uuid

  form.value.name = item.name || ""
  form.value.colour_code = item.colour_code ?? ""
  form.value.icon = item.icon || ""

  intConfigDialog.value = true
}
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>
